import { RightOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { HUBS, NAV, SECTIONS } from "@wms/domain";
import type { EntityListItem } from "@wms/domain";
import { Col, Empty, Input, Row, Tag, Typography } from "antd";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAuthStore } from "../auth/store";
import { PageHeader } from "../components/PageHeader";

interface Props {
  hub: keyof typeof HUBS;
}

/** Catalogue of every entity of the hub's sections, grouped and searchable, so the sidebar stays short. */
export function HubPage({ hub }: Props) {
  const spec = HUBS[hub];
  const can = useAuthStore((s) => s.can);
  const [q, setQ] = useState("");
  const entities = useQuery({ queryKey: ["meta", "entities"], queryFn: () => api.get<EntityListItem[]>("/meta/entities") });
  const inSidebar = new Set(NAV.flatMap((s) => s.items.filter((i) => i.kind === "entity").map((i) => i.key)));

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = (entities.data ?? []).filter(
      (e) => spec.sections.includes(e.section) && e.menu && can(`${e.permission}.read`) && (!needle || e.label_plural.toLowerCase().includes(needle) || e.name.includes(needle)),
    );
    const bySection = SECTIONS.filter((s) => spec.sections.includes(s.code))
      .map((s) => ({ section: s, items: list.filter((e) => e.section === s.code) }))
      .filter((g) => g.items.length);
    if (hub !== "admin") return bySection;
    // REQ-04: what belongs to the company (people, access, its warehouses) is separated from platform settings
    const company = new Set(["users", "roles", "warehouses", "user_sessions", "shifts", "executor_groups"]);
    const platform = new Set(["feature_options", "settings", "scheduled_jobs", "job_runs", "api_keys", "webhooks", "webhook_deliveries", "audit", "printers", "label_templates", "print_jobs", "event_subscriptions", "event_handler_runs", "integration_log", "integration_drafts"]);
    const out: typeof bySection = [];
    const companyItems = list.filter((e) => company.has(e.name));
    const platformItems = list.filter((e) => platform.has(e.name));
    if (companyItems.length) out.push({ section: { code: "company", label: "Компания: сотрудники, роли, склады" }, items: companyItems });
    if (platformItems.length) out.push({ section: { code: "platform", label: "Платформа: настройки, интеграция, журналы" }, items: platformItems });
    for (const g of bySection) {
      const rest = g.items.filter((e) => !company.has(e.name) && !platform.has(e.name));
      if (rest.length) out.push({ section: g.section, items: rest });
    }
    return out;
  }, [entities.data, q, spec.sections, can, hub]);

  return (
    <div>
      <PageHeader
        title={spec.title}
        subtitle={spec.intro}
        extra={<Input.Search allowClear autoFocus placeholder="Найти по названию" style={{ width: 320 }} onChange={(e) => setQ(e.target.value)} onSearch={setQ} />}
      />
      {hub === "admin" && !q && can("core.users.read") && (
        <Row gutter={[12, 12]} style={{ marginBottom: 26 }}>
          <Col xs={24} sm={12} lg={8} xl={6}>
            <Link to="/company" className="wms-hub-card">
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontWeight: 500 }}>Моя компания</span>
                <span style={{ display: "block", marginTop: 4, color: "var(--wms-muted)", fontSize: 12 }}>реквизиты, сотрудники, приглашения, роли</span>
              </span>
              <RightOutlined style={{ color: "var(--wms-muted)", fontSize: 12 }} />
            </Link>
          </Col>
        </Row>
      )}
      {/* KZ-DOC TMP01: the catalogue of document layouts beside «Шаблоны этикеток» of the platform group below */}
      {hub === "admin" && !q && can("document_templates.read") && (
        <Row gutter={[12, 12]} style={{ marginBottom: 26 }}>
          <Col xs={24} sm={12} lg={8} xl={6}>
            <Link to="/document-templates" className="wms-hub-card" data-testid="hub-document-templates">
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontWeight: 500 }}>Шаблоны документов</span>
                <span style={{ display: "block", marginTop: 4, color: "var(--wms-muted)", fontSize: 12 }}>макеты PDF и печати, версии, сверка бухгалтером</span>
              </span>
              <RightOutlined style={{ color: "var(--wms-muted)", fontSize: 12 }} />
            </Link>
          </Col>
        </Row>
      )}
      {groups.length === 0 && <Empty description="Ничего не найдено" />}
      {groups.map(({ section, items }) => (
        <div key={section.code} style={{ marginBottom: 26 }}>
          <Typography.Text strong style={{ display: "block", marginBottom: 10, fontSize: 13, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--wms-muted)" }}>
            {section.label} · {items.length}
          </Typography.Text>
          <Row gutter={[12, 12]}>
            {items.map((e) => (
              <Col key={e.name} xs={24} sm={12} lg={8} xl={6}>
                <Link to={`/${e.name}`} className="wms-hub-card">
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: "block", fontWeight: 500 }}>{e.label_plural}</span>
                    {(e.readonly || inSidebar.has(e.name)) && (
                      <span style={{ display: "block", marginTop: 4 }}>
                        {e.readonly ? <Tag bordered={false}>журнал</Tag> : <Tag bordered={false} color="blue">в меню</Tag>}
                      </span>
                    )}
                  </span>
                  <RightOutlined style={{ color: "var(--wms-muted)", fontSize: 12 }} />
                </Link>
              </Col>
            ))}
          </Row>
        </div>
      ))}
    </div>
  );
}
